import React, { useRef, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { SearchIcon, CompassIcon, PenToolIcon, RocketIcon, HeartHandshakeIcon, ArrowRightIcon, EyeIcon, AwardIcon, UsersIcon } from 'lucide-react';
import { AnimatedSection, AnimatedChildren, AnimatedChild } from '../components/ui/AnimatedSection';
import { TextReveal } from '../components/ui/TextReveal';
const processSteps = [{
  number: '01',
  icon: SearchIcon,
  title: 'Analyse & Beratung',
  description: 'Wir verstehen Ihre Herausforderungen, Ziele und Rahmenbedingungen. In Workshops und Gesprächen erarbeiten wir ein gemeinsames Verständnis für Ihr Projekt.'
}, {
  number: '02',
  icon: CompassIcon,
  title: 'Strategie & Konzeption',
  description: 'Gemeinsam entwickeln wir einen klaren Plan mit messbaren Zielen. Wir definieren Scope, Meilensteine und die optimale Herangehensweise.'
}, {
  number: '03',
  icon: PenToolIcon,
  title: 'Design & Entwicklung',
  description: 'Kreative und technische Umsetzung auf höchstem Niveau. Iterative Entwicklung mit regelmäßigem Feedback und transparenter Kommunikation.'
}, {
  number: '04',
  icon: RocketIcon,
  title: 'Umsetzung & Integration',
  description: 'Nahtlose Implementierung in Ihre bestehende Infrastruktur. Gründliche Tests, Schulungen und ein reibungsloser Go-Live.'
}, {
  number: '05',
  icon: HeartHandshakeIcon,
  title: 'Betreuung & Weiterentwicklung',
  description: 'Langfristige Partnerschaft für kontinuierliche Optimierung. Support, Wartung und strategische Weiterentwicklung Ihrer Lösung.'
}];
const principles = [{
  icon: EyeIcon,
  title: 'Transparenz',
  description: 'Offene Kommunikation, klare Prozesse und nachvollziehbare Entscheidungen. Sie wissen immer, wo Ihr Projekt steht.'
}, {
  icon: AwardIcon,
  title: 'Qualität',
  description: 'Kompromissloser Anspruch an Ergebnisse. Wir liefern Lösungen, auf die Sie sich verlassen können.'
}, {
  icon: UsersIcon,
  title: 'Partnerschaft',
  description: 'Zusammenarbeit auf Augenhöhe. Ihr Erfolg ist unser Erfolg – wir denken langfristig.'
}];
export function ProcessPage() {
  const processRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: processRef,
    offset: ['start center', 'end center']
  });
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return <div className="w-full" data-id="element-454">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-455">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-456">
          <AnimatedSection data-id="element-457">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest gradient-text max-w-3xl" data-id="element-458">
              Unsere Arbeitsweise
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.1} data-id="element-459">
            <p className="mt-6 text-lg md:text-xl text-white/50 max-w-2xl" data-id="element-460">
              Ein strukturierter Prozess für erfolgreiche Projekte. Transparenz,
              Qualität und Partnerschaft in jeder Phase.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-461">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-462">
          <div ref={processRef} className="max-w-3xl mx-auto relative" data-id="element-463">
            {processSteps.map((step, index) => <AnimatedSection key={step.number} delay={index * 0.1} data-id="element-464">
                <div className="relative pl-16 md:pl-24 pb-16 last:pb-0" data-id="element-465">
                  {index < processSteps.length - 1 && <div className="absolute left-6 md:left-10 top-16 bottom-0 w-px bg-white/10" data-id="element-466" />}
                  {index < processSteps.length - 1 && <motion.div className="absolute left-6 md:left-10 top-16 bottom-0 w-px bg-[#E8781A] origin-top" style={{
                scaleY: lineScaleY
              }} data-id="element-467" />}
                  <div className="absolute left-0 md:left-4 top-0 w-12 h-12 rounded-full bg-[#E8781A] text-white flex items-center justify-center font-bold text-sm" data-id="element-468">
                    {step.number}
                  </div>
                  <div data-id="element-469">
                    <div className="flex items-center gap-3 mb-3" data-id="element-470">
                      <step.icon className="w-5 h-5 text-[#E8781A]" data-id="element-471" />
                      <h3 className="text-2xl font-bold text-white" data-id="element-472">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-lg text-white/50 leading-relaxed" data-id="element-473">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-474">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-475">
          <AnimatedSection data-id="element-476">
            <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-477">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tightest gradient-text" data-id="element-478">
                Unsere Prinzipien
              </h2>
              <p className="mt-4 text-lg text-white/50" data-id="element-479">
                Die Grundlagen unserer Zusammenarbeit.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedChildren className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.1} data-id="element-480">
            {principles.map(principle => <AnimatedChild key={principle.title} data-id="element-481">
                <div className="p-8 bg-white/5 border border-white/10 rounded-2xl text-center h-full" data-id="element-482">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6" data-id="element-483">
                    <principle.icon className="w-7 h-7 text-[#E8781A]" data-id="element-484" />
                  </div>
                  <h3 className="text-xl font-semibold text-white" data-id="element-485">
                    {principle.title}
                  </h3>
                  <p className="mt-3 text-white/50" data-id="element-486">{principle.description}</p>
                </div>
              </AnimatedChild>)}
          </AnimatedChildren>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#E8781A]/10 via-[#09090B] to-[#a855f7]/10 border-y border-white/5" data-id="element-487">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-488">
          <AnimatedSection data-id="element-489">
            <div className="max-w-3xl mx-auto text-center" data-id="element-490">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest text-white" data-id="element-491">
                Bereit für ein gemeinsames Projekt?
              </h2>
              <p className="mt-6 text-lg text-white/50" data-id="element-492">
                Lassen Sie uns in einem ersten Gespräch herausfinden, wie wir
                Sie unterstützen können.
              </p>
              <div className="mt-10" data-id="element-493">
                <Link to="/kontakt" className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#09090B] font-medium rounded-lg hover:bg-white/90 transition-colors" data-id="element-494">
                  Gespräch vereinbaren
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-495" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}